import React, { useState, useCallback } from 'react';
import { makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import {
    Box,
    TextareaAutosize,
    AccordionDetails,
    AccordionSummary as MuiAccordionSummary,
    Accordion as MuiAccordion,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import DpxSwitch from 'src/components/DpxSwitch';
import { TradeInVehicle } from './TradeInVehicle';
import { IQuestionItem } from './types';
import {
    TEXT_QUESTION,
    TRADE_IN_VEHICLE_QUESTION,
    YES_NO_QUESTION,
} from 'src/constants/lead/evaluation';

const Accordion = withStyles({
    root: {
        borderBottom: '1px solid rgba(0, 0, 0, .125)',
        boxShadow: 'none',
        '&:not(:last-child)': {
            borderBottom: 0,
        },
        '&:before': {
            display: 'none',
        },
        '&$expanded': {
            margin: 'auto',
        },
    },
    expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
    root: {
        marginBottom: -1,
        '&$expanded': {},
    },
    content: {
        '&$expanded': {
            margin: '0 0',
        },
    },
    expanded: {},
})(MuiAccordionSummary);

const useStyles = makeStyles((theme: Theme) => ({
    summary: {
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        alignItems: 'center',
        display: 'flex',
        width: '100%',
    },
    switchContainer: {
        alignItems: 'center',
        display: 'flex',
    },
    textarea: {
        ...theme.typography.body1,
        backgroundColor: theme.palette.background.default,
        border: `solid 1px ${theme.palette.divider}`,
        color: theme.palette.text.primary,
        padding: '10px 15px',
        borderRadius: 3,
        resize: 'none',
        width: '100%',
    },
}));

export const QuestionItem = ({
    question,
    vehicle,
    handleChange,
    handleChangeVehicle,
    isEditable,
}: IQuestionItem) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [isChecked, setChecked] = useState<boolean>(question.answer ?? true);
    const [reason, setReason] = useState<string>(question.reason || '');

    const toggleChecked = useCallback(() => {
        setChecked((prev) => {
            const newChecked = !prev;
            handleChange(question._id, newChecked, reason);
            return newChecked;
        });
    }, [question._id, reason, handleChange]);

    const handleReasonChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        const newReason = event.target.value;
        setReason(newReason);
        handleChange(question._id, isChecked, newReason);
    };

    const getAccordionOpen = (): boolean => {
        if (question.type === TRADE_IN_VEHICLE_QUESTION) return isChecked;
        if (question.type === YES_NO_QUESTION) return !isChecked;
        if (question.type === TEXT_QUESTION) return isChecked;
        return true;
    };

    const isAccordionOpen = getAccordionOpen();

    return (
        <Box>
            <Accordion square expanded={isAccordionOpen}>
                <AccordionSummary>
                    <Box className={classes.summary}>
                        <Typography variant="body1" color="textPrimary">
                            {question.question}
                        </Typography>

                        <Box className={classes.switchContainer}>
                            <Typography variant="h6" color="textPrimary">
                                {t('No')}
                            </Typography>
                            <DpxSwitch
                                checked={isChecked}
                                onChange={toggleChecked}
                                disabled={!isEditable}
                            />
                            <Typography variant="h6" color="textPrimary">
                                {t('Yes')}
                            </Typography>
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {!isChecked && question.type === YES_NO_QUESTION && (
                        <TextareaAutosize
                            className={classes.textarea}
                            placeholder={t('Why')}
                            minRows={2}
                            maxRows={2}
                            value={reason}
                            onChange={handleReasonChange}
                            disabled={!isEditable}
                        />
                    )}
                    {isChecked && question.type === TEXT_QUESTION && (
                        <TextareaAutosize
                            className={classes.textarea}
                            placeholder={question.question}
                            minRows={2}
                            maxRows={2}
                            value={reason}
                            onChange={handleReasonChange}
                            disabled={!isEditable}
                        />
                    )}
                    {isChecked &&
                        question.type === TRADE_IN_VEHICLE_QUESTION && (
                            <TradeInVehicle
                                vehicle={vehicle}
                                handleChangeVehicle={handleChangeVehicle}
                                isEditable={isEditable}
                            />
                        )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
